import dayjs, { ConfigType as DateType, Dayjs } from 'dayjs';
import React, { useMemo } from 'react';

import { PerDayComponentProps } from '../DatePicker';
import CalendarDay from './CalendarDay';

import cx from '../../utils/classnames';
import { generateMonthWeeks, getDaySelectedState, getDayState } from './helpers';

import styles from './calendarBody.module.scss';

type Props = {
  /** Custom style for wrapper */
  className?: string;
  /** Date to define the calendar month and year
   * (eg. 2019-10-29, the calendar will be rendered as October, 2019 )
   */
  calendarDate: DateType;
  /** Custom style for calendar day */
  dayItemClassName?: string;
  /** End date of selected range */
  endDate?: DateType;
  /** Date which is being hovered */
  hoveredDate?: DateType;
  /** Date to reference (usually, be used as current date) */
  referenceDate?: DateType;
  /** Start date of selected range */
  startDate?: DateType;
  /** callback function when calendar day is clicked */
  onClickDayItem: (event: React.MouseEvent, day: Dayjs) => void;
  /** callback function when calendar day is being hover */
  onHoverDayItem?: (event: React.MouseEvent, day?: Dayjs) => void;
  /** week type:
   * 'week': start from Sunday
   * 'isoWeek': start from Monday
   */
  weekType?: 'week' | 'isoWeek';
  /** Date to end of reference  */
  referenceEndDate?: DateType;
  /** Flag to highlight the current date */
  shouldShowCurrentDate?: boolean;
  /** Custom style for wrapper of calendar day */
  dayWrapperClassName?: string;
  /** Extra component to display on calendar per day */
  PerDayComponent?: React.ComponentType<PerDayComponentProps>;
  /** Date Selection Highlight type (default circle) */
  highlightType?: 'circle' | 'full';
};

// TODO: Check performance issue when applied with DatePicker
function CalendarBody(props: Props) {
  const {
    className,
    calendarDate,
    endDate,
    hoveredDate,
    dayItemClassName,
    onClickDayItem,
    onHoverDayItem,
    referenceDate,
    startDate,
    weekType,
    referenceEndDate,
    shouldShowCurrentDate,
    dayWrapperClassName,
    PerDayComponent,
    highlightType = 'circle',
    ...rest
  } = props;

  const calendarDay = useMemo(() => dayjs(calendarDate), [calendarDate]);
  const weeks = useMemo(() => generateMonthWeeks(calendarDay, weekType), [calendarDay, weekType]);

  const referenceDay = useMemo(() => referenceDate && dayjs(referenceDate), [referenceDate]);
  const referenceEndDay = useMemo(() => referenceEndDate && dayjs(referenceEndDate), [
    referenceEndDate,
  ]);
  const startRangeDay = useMemo(() => startDate && dayjs(startDate), [startDate]);
  const endRangeDay = useMemo(() => endDate && dayjs(endDate), [endDate]);

  // hoveredDate can be changed many times, so we don't need to use useMemo()
  const hoveredDay = hoveredDate && dayjs(hoveredDate);

  return (
    <table className={cx(className, styles.wrapper)} {...rest}>
      <tbody data-testid="calendarBody-table-body">
        {weeks.map((days, index) => (
          <tr key={index} data-testid={'CalendarBody-tr'}>
            {days.map((day, index) => {
              const dayState = getDayState(
                day,
                calendarDay,
                referenceDay,
                referenceEndDay,
                weekType,
              );
              const selectedState = getDaySelectedState(
                day,
                startRangeDay,
                endRangeDay,
                hoveredDay,
              );
              return (
                <CalendarDay
                  // Using index as a key in order to fix cross-month selected date issue,
                  // due to some date in week might be duplicated
                  // pls see: generateMonthWeeks (./helpers)
                  key={`${day.format('D')}-${index}`}
                  day={day}
                  dayState={dayState}
                  itemClassName={dayItemClassName}
                  className={dayWrapperClassName}
                  selectedState={selectedState}
                  onClick={onClickDayItem}
                  onHover={onHoverDayItem}
                  extraComponent={
                    PerDayComponent && dayState !== 'invalidMonth' ? (
                      <PerDayComponent day={day} selectedState={selectedState} />
                    ) : (
                      undefined
                    )
                  }
                  shouldShowCurrentDate={shouldShowCurrentDate}
                  highlightType={highlightType}
                />
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default CalendarBody;
